/**
 * Gestor de Programación de Servicios de Entrega
 * Lee las configuraciones desde settings_schema.json y las aplica
 */

class DeliverySchedulingManager {
  constructor() {
    this.config = null;
    this.customHolidays = [];
    this.specificDateOverrides = [];
    this.loadConfig();
  }

  /**
   * Carga la configuración desde el JSON expuesto en el DOM
   */
  loadConfig() {
    const configElement = document.getElementById('delivery-scheduling-config');
    if (!configElement) {
      console.warn('Configuración de programación de servicios no encontrada');
      return;
    }

    try {
      this.config = JSON.parse(configElement.textContent);
      // Normalizar valores booleanos (pueden venir como strings "true"/"false")
      this.normalizeConfig();
      this.parseCustomHolidays();
      this.parseSpecificDateOverrides();
      console.log('Configuración de servicios cargada:', this.config);
      console.log('Feriados personalizados:', this.customHolidays);
    } catch (error) {
      console.error('Error al cargar configuración de servicios:', error);
    }
  }

  /**
   * Normaliza los valores booleanos en la configuración
   */
  normalizeConfig() {
    if (!this.config) return;

    // Normalizar enabled principal
    if (typeof this.config.enabled === 'string') {
      this.config.enabled = this.config.enabled === 'true';
    }

    // Normalizar useApiHolidays
    if (typeof this.config.useApiHolidays === 'string') {
      this.config.useApiHolidays = this.config.useApiHolidays === 'true';
    }

    // Normalizar servicios
    if (this.config.services) {
      Object.keys(this.config.services).forEach(serviceId => {
        const service = this.config.services[serviceId];
        
        // Normalizar enabled del servicio
        if (typeof service.enabled === 'string') {
          service.enabled = service.enabled === 'true';
        }

        // Normalizar días
        if (service.days) {
          Object.keys(service.days).forEach(dayName => {
            const day = service.days[dayName];
            if (typeof day.enabled === 'string') {
              day.enabled = day.enabled === 'true';
            }
          });
        }
      });
    }
  }

  /**
   * Parsea los feriados personalizados desde el texto
   */
  parseCustomHolidays() {
    if (!this.config || !this.config.customHolidays) return;

    const holidaysText = this.config.customHolidays.trim();
    if (!holidaysText) return;

    // Acepta saltos de línea, comas o punto y coma como separadores
    this.customHolidays = holidaysText
      .split(/[\n,;]+/)
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => this.parseHolidayDate(line))
      .filter(holiday => holiday !== null);
    
    console.log('Feriados parseados:', this.customHolidays);
  }

  /**
   * Parsea una fecha de feriado (formato DD/MM/YYYY, DD-MM-YYYY o DD/MM/****)
   */
  parseHolidayDate(dateString) {
    if (!dateString) return null;
    const normalized = String(dateString).trim().replace(/-/g, '/');
    const parts = normalized.split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JS son 0-indexed
    const year = parts[2].trim();

    if (isNaN(day) || isNaN(month) || month < 0 || month > 11 || day < 1 || day > 31) {
      return null;
    }

    if (year === '****') {
      return { day, month, recurring: true };
    }

    const yearNum = parseInt(year, 10);
    if (isNaN(yearNum)) return null;
    return { day, month, year: yearNum, recurring: false };
  }

  /**
   * Parsea las configuraciones de fechas específicas
   */
  parseSpecificDateOverrides() {
    if (!this.config || !this.config.specificDateOverrides) return;

    const overridesText = this.config.specificDateOverrides.trim();
    if (!overridesText) return;

    this.specificDateOverrides = overridesText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        const parts = line.split('|');
        if (parts.length !== 3) return null;

        const dateParts = parts[0].split('/');
        if (dateParts.length !== 3) return null;

        const timeParts = parts[1].split(':');
        if (timeParts.length !== 2) return null;

        return {
          date: {
            day: parseInt(dateParts[0], 10),
            month: parseInt(dateParts[1], 10) - 1,
            year: parseInt(dateParts[2], 10)
          },
          cutoff: {
            hour: parseInt(timeParts[0], 10),
            minute: parseInt(timeParts[1], 10)
          },
          service: parseInt(parts[2], 10)
        };
      })
      .filter(override => override !== null);
  }

  /**
   * Verifica si una fecha es un feriado
   */
  isHoliday(date) {
    if (!this.config || !this.config.enabled) {
      console.log('isHoliday: Config no habilitada o no existe');
      return false;
    }

    // Verificar feriados personalizados
    for (const holiday of this.customHolidays) {
      if (!holiday) continue;
      
      if (holiday.recurring) {
        if (date.getDate() === holiday.day && date.getMonth() === holiday.month) {
          console.log(`isHoliday: Fecha ${date.toDateString()} es feriado recurrente (${holiday.day}/${holiday.month + 1})`);
          return true;
        }
      } else {
        if (date.getDate() === holiday.day && 
            date.getMonth() === holiday.month && 
            date.getFullYear() === holiday.year) {
          console.log(`isHoliday: Fecha ${date.toDateString()} es feriado específico`);
          return true;
        }
      }
    }

    // Si está habilitado, también verificar API de feriados
    if (this.config.useApiHolidays) {
      // Esta función debería ser llamada desde el código existente que usa la API
      // Por ahora retornamos false y dejamos que el código existente maneje la API
    }

    return false;
  }

  /**
   * Verifica si una fecha es un feriado específico de un servicio
   */
  isServiceSpecificHoliday(date, serviceId) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      return false;
    }

    const service = this.config.services[serviceId];
    if (!service.specificHolidays) return false;

    const holidaysText = service.specificHolidays.trim();
    if (!holidaysText) return false;

    const serviceHolidays = holidaysText.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => this.parseHolidayDate(line));

    for (const holiday of serviceHolidays) {
      if (!holiday) continue;

      if (holiday.recurring) {
        if (date.getDate() === holiday.day && date.getMonth() === holiday.month) {
          return true;
        }
      } else {
        if (date.getDate() === holiday.day && 
            date.getMonth() === holiday.month && 
            date.getFullYear() === holiday.year) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Obtiene el horario de corte para un servicio en un día específico
   */
  getCutoffTime(serviceId, date) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      return null;
    }

    const service = this.config.services[serviceId];
    if (!service.enabled) return null;

    // Verificar si hay una configuración específica para esta fecha
    const override = this.getSpecificDateOverride(date, serviceId);
    if (override) {
      return {
        hour: override.cutoff.hour,
        minute: override.cutoff.minute
      };
    }

    // Obtener el día de la semana (0 = domingo, 1 = lunes, etc.)
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];

    // Verificar si el día está habilitado
    if (!service.days[dayName] || !service.days[dayName].enabled) {
      return null;
    }

    // Si hay un horario específico para este día, usarlo
    const dayCutoff = service.days[dayName].cutoff;
    if (dayCutoff && dayCutoff.trim() !== '') {
      const timeParts = dayCutoff.split(':');
      if (timeParts.length === 2) {
        return {
          hour: parseInt(timeParts[0], 10),
          minute: parseInt(timeParts[1], 10)
        };
      }
    }

    // Usar el horario por defecto
    return {
      hour: service.defaultCutoff.hour,
      minute: service.defaultCutoff.minute
    };
  }

  /**
   * Obtiene una configuración de fecha específica si existe
   */
  getSpecificDateOverride(date, serviceId) {
    for (const override of this.specificDateOverrides) {
      if (override.service !== serviceId) continue;

      const overrideDate = new Date(
        override.date.year,
        override.date.month,
        override.date.day
      );

      if (date.getDate() === overrideDate.getDate() &&
          date.getMonth() === overrideDate.getMonth() &&
          date.getFullYear() === overrideDate.getFullYear()) {
        return override;
      }
    }
    return null;
  }

  /**
   * Verifica si un servicio está disponible en una fecha específica
   */
  isServiceAvailable(serviceId, date) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      console.log(`isServiceAvailable: Config o servicio ${serviceId} no existe`);
      return false;
    }

    const service = this.config.services[serviceId];
    if (!service.enabled) {
      console.log(`isServiceAvailable: Servicio ${serviceId} está deshabilitado`);
      return false;
    }

    // Verificar si es un feriado general
    if (this.isHoliday(date)) {
      console.log(`isServiceAvailable: Fecha ${date.toDateString()} es feriado general para servicio ${serviceId}`);
      return false;
    }

    // Verificar si es un feriado específico del servicio
    if (this.isServiceSpecificHoliday(date, serviceId)) {
      console.log(`isServiceAvailable: Fecha ${date.toDateString()} es feriado específico para servicio ${serviceId}`);
      return false;
    }

    // Verificar si el día de la semana está habilitado
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];
    
    const dayEnabled = service.days[dayName] && service.days[dayName].enabled;
    if (!dayEnabled) {
      console.log(`isServiceAvailable: Día ${dayName} no está habilitado para servicio ${serviceId}`);
    }
    
    return dayEnabled;
  }

  /**
   * Obtiene la configuración de un servicio
   */
  getServiceConfig(serviceId) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      return null;
    }
    return this.config.services[serviceId];
  }

  /**
   * Obtiene el mensaje personalizado para un servicio en un día específico
   */
  getDayMessage(serviceId, date) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      return null;
    }

    const service = this.config.services[serviceId];
    if (!service.enabled) return null;

    // Obtener el día de la semana (0 = domingo, 1 = lunes, etc.)
    const dayOfWeek = date.getDay();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];

    // Verificar si el día está configurado
    if (!service.days[dayName]) {
      return null;
    }

    // Retornar el mensaje del día, o null si no está configurado
    return service.days[dayName].message || null;
  }

  /**
   * Encuentra el próximo día disponible para un servicio considerando feriados
   * @param {string|number} serviceId - ID del servicio
   * @param {Date} startDate - Fecha desde donde empezar a buscar
   * @returns {Date|null} - Próximo día disponible o null si no se encuentra
   */
  findNextAvailableDay(serviceId, startDate) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      console.log(`findNextAvailableDay: Config o servicio ${serviceId} no existe`);
      return null;
    }

    const service = this.config.services[serviceId];
    if (!service.enabled) {
      console.log(`findNextAvailableDay: Servicio ${serviceId} está deshabilitado`);
      return null;
    }

    console.log(`findNextAvailableDay: Buscando próximo día disponible para servicio ${serviceId} desde ${startDate.toDateString()}`);

    // Buscar en los próximos 7 días
    for (let i = 1; i <= 7; i++) {
      const checkDate = new Date(startDate);
      checkDate.setDate(startDate.getDate() + i);
      checkDate.setHours(0, 0, 0, 0);

      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const dayName = dayNames[checkDate.getDay()];
      
      // Verificar si este día está disponible
      const isAvailable = this.isServiceAvailable(serviceId, checkDate);
      console.log(`findNextAvailableDay: ${dayName} ${checkDate.toDateString()} - Disponible: ${isAvailable}`);
      
      if (isAvailable) {
        console.log(`findNextAvailableDay: Encontrado día disponible: ${dayName} ${checkDate.toDateString()}`);
        return checkDate;
      }
    }

    console.log(`findNextAvailableDay: No se encontró ningún día disponible en los próximos 7 días`);
    return null;
  }

  /**
   * Mensaje post-cutoff / próximo día de ENTREGA (respeta feriados y días apagados).
   * - Si la entrega es mañana: usa el mensaje custom de HOY (si existe).
   * - Si salta días (ej. feriado 25/07): genera "LLEGA EL {día real}".
   * Nunca usa getDayMessage del día de entrega (ese texto es "si compras ESE día").
   * @param {string|number} serviceId - ID del servicio
   * @param {Date} currentDate - Fecha/hora de compra
   * @returns {string|null}
   */
  getNextAvailableDayMessage(serviceId, currentDate) {
    console.log(`getNextAvailableDayMessage: Buscando mensaje para servicio ${serviceId} desde ${currentDate.toDateString()}`);
    const nextAvailableDate = this.findNextAvailableDay(serviceId, currentDate);
    if (!nextAvailableDate) {
      console.log(`getNextAvailableDayMessage: No se encontró día disponible`);
      return null;
    }

    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const deliveryDayName = dayNames[nextAvailableDate.getDay()];
    console.log(`getNextAvailableDayMessage: Día de entrega: ${deliveryDayName} ${nextAvailableDate.toDateString()}`);

    const startOfCurrent = new Date(currentDate);
    startOfCurrent.setHours(0, 0, 0, 0);

    const tomorrow = new Date(startOfCurrent);
    tomorrow.setDate(startOfCurrent.getDate() + 1);

    const isDeliveryTomorrow =
      nextAvailableDate.getFullYear() === tomorrow.getFullYear() &&
      nextAvailableDate.getMonth() === tomorrow.getMonth() &&
      nextAvailableDate.getDate() === tomorrow.getDate();

    // Caso normal (sin saltar feriados): mensaje configurado de hoy
    if (isDeliveryTomorrow) {
      const customMessage = this.getDayMessage(serviceId, currentDate);
      if (customMessage) {
        console.log(`getNextAvailableDayMessage: Mensaje custom de hoy: ${customMessage}`);
        return customMessage;
      }
    }

    const serviceConfig = this.getServiceConfig(serviceId);
    const serviceName = serviceConfig && serviceConfig.name ? String(serviceConfig.name).toUpperCase() : '';
    const isFlash = serviceName === 'FLASH' || serviceId === '2' || serviceId === 2;
    const daysDiff = Math.round((nextAvailableDate - startOfCurrent) / (1000 * 60 * 60 * 24));

    const message = daysDiff === 1
      ? (isFlash
          ? `LLEGA MAÑANA ${deliveryDayName.toUpperCase()} ANTES DE LAS 12:30 PM`
          : `LLEGA MAÑANA ${deliveryDayName.toUpperCase()} DURANTE EL DIA`)
      : (isFlash
          ? `LLEGA EL ${deliveryDayName.toUpperCase()} ANTES DE LAS 12:30 PM`
          : `LLEGA EL ${deliveryDayName.toUpperCase()} DURANTE EL DIA`);

    console.log(`getNextAvailableDayMessage: Mensaje generado (salta días/feriado): ${message}`);
    return message;
  }

  /**
   * Obtiene el mensaje de entrega según la hora actual
   * @param {string|number} serviceId - ID del servicio
   * @param {Date} currentDate - Fecha actual
   * @returns {Object} - Objeto con message, showCountdown, tomorrowMessage
   */
  getDeliveryMessage(serviceId, currentDate) {
    const now = currentDate || new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinutes;
    
    // Verificar si el servicio está disponible hoy
    const isAvailableToday = this.isServiceAvailable(serviceId, now);
    
    // Si no está disponible hoy, retornar mensaje del próximo día
    if (!isAvailableToday) {
      const nextAvailableDate = this.findNextAvailableDay(serviceId, now);
      if (nextAvailableDate) {
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const nextDayName = dayNames[nextAvailableDate.getDay()];
        const daysDiff = Math.ceil((nextAvailableDate - now) / (1000 * 60 * 60 * 24));
        
        const serviceConfig = this.getServiceConfig(serviceId);
        const serviceName = serviceConfig?.name || (serviceId === '2' || serviceId === 2 ? 'FLASH' : 'EXPRESS');
        const isFlash = serviceName.toUpperCase() === 'FLASH';
        
        return {
          message: isFlash ? "ENVÍO FLASH" : "ENVÍO EXPRESS",
          showCountdown: false,
          showTomorrow: true,
          tomorrowMessage: daysDiff === 1 ?
            (isFlash ? `LLEGA MAÑANA ${nextDayName.toUpperCase()} ANTES DE LAS 12:30 PM` : `LLEGA MAÑANA ${nextDayName.toUpperCase()} DURANTE EL DIA`)
            : (isFlash ? `LLEGA EL ${nextDayName.toUpperCase()} ANTES DE LAS 12:30 PM` : `LLEGA EL ${nextDayName.toUpperCase()} DURANTE EL DIA`)
        };
      }
    }
    
    // De 0:00 a 10:00 - "Llega hoy [DÍA] antes de las 12:30pm" (con nombre del día, sin countdown)
    if (currentTime < 10 * 60) {
      const serviceConfig = this.getServiceConfig(serviceId);
      const isFlash = serviceConfig?.name?.toUpperCase() === 'FLASH' || serviceId === '2' || serviceId === 2;
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const todayName = dayNames[now.getDay()];
      
      return {
        message: isFlash ? "ENVÍO FLASH" : "ENVÍO EXPRESS",
        showCountdown: false,
        showTomorrow: true,
        tomorrowMessage: isFlash ? `LLEGA HOY ${todayName.toUpperCase()} ANTES DE LAS 12:30 PM` : `LLEGA HOY ${todayName.toUpperCase()} DURANTE EL DIA`
      };
    }
    
    // Obtener el cutoff time
    const cutoff = this.getCutoffTime(serviceId, now);
    if (!cutoff) {
      return {
        message: "ENVÍO",
        showCountdown: false,
        showTomorrow: false
      };
    }
    
    const cutoffTime = new Date(now);
    cutoffTime.setHours(cutoff.hour, cutoff.minute, 0, 0);
    const currentTimeDate = new Date(now);
    
    // Después del cutoff
    if (currentTimeDate >= cutoffTime) {
      const nextAvailableDate = this.findNextAvailableDay(serviceId, now);
      if (nextAvailableDate) {
        const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const nextDayName = dayNames[nextAvailableDate.getDay()];
        const daysDiff = Math.ceil((nextAvailableDate - now) / (1000 * 60 * 60 * 24));
        
        const serviceConfig = this.getServiceConfig(serviceId);
        const isFlash = serviceConfig?.name?.toUpperCase() === 'FLASH' || serviceId === '2' || serviceId === 2;
        
        return {
          message: isFlash ? "ENVÍO FLASH" : "ENVÍO EXPRESS",
          showCountdown: false,
          showTomorrow: true,
          tomorrowMessage: daysDiff === 1 ?
            (isFlash ? `LLEGA MAÑANA ${nextDayName.toUpperCase()} ANTES DE LAS 12:30 PM` : `LLEGA MAÑANA ${nextDayName.toUpperCase()} DURANTE EL DIA`)
            : (isFlash ? `LLEGA EL ${nextDayName.toUpperCase()} ANTES DE LAS 12:30 PM` : `LLEGA EL ${nextDayName.toUpperCase()} DURANTE EL DIA`)
        };
      }
    }
    
    // Durante el día - mostrar mensaje normal con countdown (desde las 10:00 AM)
    const serviceConfig = this.getServiceConfig(serviceId);
    const isFlash = serviceConfig?.name?.toUpperCase() === 'FLASH' || serviceId === '2' || serviceId === 2;
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const todayName = dayNames[now.getDay()];
    
    return {
      message: isFlash ? "LLEGA EN 2H CON ENVÍO FLASH" : `RECIBE HOY ${todayName.toUpperCase()} CON ENVÍO EXPRESS`,
      showCountdown: true,
      showTomorrow: true,
      tomorrowMessage: isFlash ? 
        `LLEGA HOY ${todayName.toUpperCase()} ANTES DE LAS 12:30 PM` : 
        `LLEGA HOY ${todayName.toUpperCase()} DURANTE EL DIA`,
      targetTime: cutoffTime.getTime()
    };
  }

  /**
   * Obtiene el texto de horarios de corte para mostrar en el modal
   * @param {string|number} serviceId - ID del servicio
   * @returns {string} - Texto con los horarios de corte
   */
  getCutoffScheduleText(serviceId) {
    if (!this.config || !this.config.services || !this.config.services[serviceId]) {
      return '';
    }

    const service = this.config.services[serviceId];
    
    // Si hay un texto personalizado configurado, usarlo
    if (service.modalScheduleText && service.modalScheduleText.trim() !== '') {
      return service.modalScheduleText.trim();
    }
    
    // Si no hay texto personalizado, generar automáticamente
    const dayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    let scheduleParts = [];
    
    // Agrupar días con el mismo horario
    const scheduleMap = new Map();
    
    dayKeys.forEach((dayKey, index) => {
      const day = service.days[dayKey];
      if (day && day.enabled) {
        const cutoff = day.cutoff && day.cutoff.trim() !== '' 
          ? day.cutoff 
          : `${String(service.defaultCutoff.hour).padStart(2, '0')}:${String(service.defaultCutoff.minute).padStart(2, '0')}`;
        
        if (!scheduleMap.has(cutoff)) {
          scheduleMap.set(cutoff, []);
        }
        scheduleMap.get(cutoff).push(dayNames[index]);
      }
    });
    
    // Construir el texto
    scheduleMap.forEach((days, cutoff) => {
      if (days.length === 1) {
        scheduleParts.push(`${days[0]} ${cutoff}`);
      } else if (days.length === 2) {
        scheduleParts.push(`${days[0]} y ${days[1]} ${cutoff}`);
      } else if (days.length === 5 && days.includes('Lunes') && days.includes('Viernes')) {
        scheduleParts.push(`Lun a Vie ${cutoff}`);
      } else {
        scheduleParts.push(`${days.join(', ')} ${cutoff}`);
      }
    });
    
    return scheduleParts.join(' / ') || 'No disponible';
  }
}

// Crear instancia global (el JSON de config ya está en el DOM cuando carga este script)
window.deliverySchedulingManager = new DeliverySchedulingManager();

// Función para recargar la configuración (útil cuando cambian los valores en el schema)
window.reloadDeliverySchedulingConfig = function() {
  if (window.deliverySchedulingManager) {
    window.deliverySchedulingManager.loadConfig();
  }
};

